import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import QuillEditor from "../../common/QuillEditor";
import { Save, ArrowLeft, Loader2, ImagePlus } from "lucide-react";

export default function AddServiceCategoryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    nameTr: "",
    nameEn: "",
    nameAr: "",
    descriptionTr: "",
    descriptionEn: "",
    descriptionAr: "",
    colorCode: "#6366f1",
    slug: "",
    sortOrder: 1,
    isActive: true,
    iconFile: null,
    iconPreview: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, catRes] = await Promise.all([
          api.get("/setting"),
          id ? api.get(`/servicecategory/${id}`) : Promise.resolve(null),
        ]);
        setSettings(settingsRes.data);

        if (catRes) {
          const d = catRes.data;
          setFormData((p) => ({
            ...p,
            nameTr: d.nameTr || "",
            nameEn: d.nameEn || "",
            nameAr: d.nameAr || "",
            descriptionTr: d.descriptionTr || "",
            descriptionEn: d.descriptionEn || "",
            descriptionAr: d.descriptionAr || "",
            colorCode: d.colorCode || "#6366f1",
            slug: d.slug || "",
            sortOrder: d.sortOrder || 1,
            isActive: d.isActive,
            iconPreview: d.iconPath
              ? `${process.env.REACT_APP_UPLOAD_URL}/${d.iconPath}`
              : "",
          }));
        }
      } catch {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateSlug = (title) =>
    title
      .toLowerCase()
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;
    const newData = {
      ...formData,
      [name]:
        type === "file" ? files[0] : type === "checkbox" ? checked : value,
    };

    if (type === "file" && files[0]) {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setFormData((p) => ({ ...p, iconPreview: ev.target.result }));
      reader.readAsDataURL(files[0]);
    }

    if ((name === "nameTr" || name === "nameEn") && !formData.slug)
      newData.slug = generateSlug(value);
    setFormData(newData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = new FormData();
    data.append("NameTr", formData.nameTr);
    data.append("NameEn", formData.nameEn);
    data.append("NameAr", formData.nameAr);
    data.append("DescriptionTr", formData.descriptionTr);
    data.append("DescriptionEn", formData.descriptionEn);
    data.append("DescriptionAr", formData.descriptionAr);
    data.append("ColorCode", formData.colorCode);
    data.append("Slug", formData.slug);
    data.append("SortOrder", formData.sortOrder || 1);
    data.append("IsActive", formData.isActive);
    if (formData.iconFile) data.append("iconFile", formData.iconFile);

    try {
      if (id) {
        await api.put(`/servicecategory/${id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Kategori güncellendi");
      } else {
        await api.post("/servicecategory", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Kategori eklendi");
      }
      navigate("/service-category-list");
    } catch {
      toast.error("Kategori kaydedilirken hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="page-loading">
        <div className="loading-spinner" />
        <p>Yükleniyor...</p>
      </div>
    );
  if (!settings)
    return (
      <div className="page-loading">
        <p>Ayarlar bulunamadı.</p>
      </div>
    );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>{id ? "Kategori Düzenle" : "Yeni Kategori Ekle"}</h2>
          <p className="page-subtitle">
            {id
              ? "Mevcut hizmet kategorisini düzenleyin"
              : "Yeni bir hizmet kategorisi oluşturun"}
          </p>
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/service-category-list")}
        >
          <ArrowLeft size={18} /> Listeye Dön
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-layout-2col">
          {/* Sol Kolon - İçerik */}
          <div className="form-main">
            {settings.turkish && (
              <div className="content-card">
                <h3 className="card-title">🇹🇷 Türkçe İçerik</h3>
                <div className="form-field">
                  <label>Kategori Adı</label>
                  <input
                    type="text"
                    name="nameTr"
                    value={formData.nameTr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Türkçe kategori adı"
                  />
                </div>
                <div className="form-field">
                  <label>Açıklama</label>
                  <QuillEditor
                    value={formData.descriptionTr}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionTr: c }))
                    }
                    placeholder="Türkçe açıklama"
                  />
                </div>
              </div>
            )}

            {settings.english && (
              <div className="content-card">
                <h3 className="card-title">🇬🇧 English Content</h3>
                <div className="form-field">
                  <label>Category Name</label>
                  <input
                    type="text"
                    name="nameEn"
                    value={formData.nameEn}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="English category name"
                  />
                </div>
                <div className="form-field">
                  <label>Description</label>
                  <QuillEditor
                    value={formData.descriptionEn}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionEn: c }))
                    }
                    placeholder="English description"
                  />
                </div>
              </div>
            )}

            {settings.arabic && (
              <div className="content-card">
                <h3 className="card-title">🇸🇦 المحتوى العربي</h3>
                <div className="form-field">
                  <label>اسم الفئة</label>
                  <input
                    type="text"
                    name="nameAr"
                    value={formData.nameAr}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="اسم الفئة"
                  />
                </div>
                <div className="form-field">
                  <label>الوصف</label>
                  <QuillEditor
                    value={formData.descriptionAr}
                    onChange={(c) =>
                      setFormData((p) => ({ ...p, descriptionAr: c }))
                    }
                    placeholder="الوصف"
                    dir="rtl"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Sağ Kolon - Ayarlar */}
          <div className="form-sidebar">
            <div className="content-card sticky-card">
              <h3 className="card-title">İkon</h3>
              <div className="image-upload-area">
                {formData.iconPreview ? (
                  <div className="image-preview">
                    <img src={formData.iconPreview} alt="İkon" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          iconFile: null,
                          iconPreview: "",
                        }))
                      }
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="upload-placeholder">
                    <ImagePlus size={32} />
                    <span>İkon Yükle</span>
                    <input
                      type="file"
                      name="iconFile"
                      accept="image/*"
                      onChange={handleChange}
                      hidden
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="content-card">
              <h3 className="card-title">Ayarlar</h3>
              <div className="form-field">
                <label>Renk Kodu</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="color"
                    name="colorCode"
                    value={formData.colorCode}
                    onChange={handleChange}
                    style={{
                      width: 40,
                      height: 40,
                      padding: 2,
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      cursor: "pointer",
                    }}
                  />
                  <input
                    type="text"
                    name="colorCode"
                    value={formData.colorCode}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="#6366f1"
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Sıralama</label>
                <input
                  type="number"
                  name="sortOrder"
                  value={formData.sortOrder}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>
              <div className="form-field">
                <label>URL (Slug)</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="kategori-slug"
                />
              </div>
              <div className="form-field">
                <label className="toggle-label">
                  <span>Aktif</span>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                </label>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="spin" /> Kaydediliyor...
                </>
              ) : (
                <>
                  <Save size={18} /> {id ? "Güncelle" : "Kaydet"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
